import { Typography, TextSize } from '@shared/index';
import { formatNum } from '@app/utils/math';
import { daysUntilEndOfMonth } from '@app/utils/date';
import { ProgressGraph } from '../progress-graph';
import { getCurrentDate } from '@shared/utils/getCurrentDate';
import { EditMonthlyResult } from '../progress-card/edit-monthly-result';
import { Pencil } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { 
  useGetTeamMonthlyResults,
  useGetTeamMonthlyGoal,
  useUpdateManagersMonthlyPlan,
} from '@entities/managers-perfomance';
import { QUERY_KEY_MANAGER_PERFORMANCE } from '@entities/managers-perfomance/query-keys';

export const MonthlyProgressResult = () => {
  const { year, month } = getCurrentDate();
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: teamMonthlyGoal } = useGetTeamMonthlyGoal({
    year,
    month,
  });

  const { data: teamMonthlyResults = [] } = useGetTeamMonthlyResults({
    year,
    month,
  });

  const updateTeamMonthlyPlan = useUpdateManagersMonthlyPlan();

  const { managersData, totalHashrate } = useMemo(() => {
    if (!teamMonthlyResults.length) {
      return { managersData: [], totalHashrate: 0 };
    }

    const mapped = teamMonthlyResults.map(result => ({
      id: result.manager_id,
      name: result.manager_name,
      result: result.hashrate,
    }));

    const total = mapped.reduce((sum, manager) => sum + manager.result, 0);

    return {
      managersData: mapped,
      totalHashrate: total,
    };
  }, [teamMonthlyResults]);

  const {
    plan = 0,
    hashrate: currentValue = 0,
    percentage: percent = 0,
    remaining_hashrate: remainingValue = 0,
  } = teamMonthlyGoal ?? {};

  const remainingPercent = plan > 0 ? (remainingValue / plan) * 100 : 0;
  const planValue = plan > 0 ? formatNum(plan) : '';

  const daysLeft = daysUntilEndOfMonth();

  const infoCards = [
    { label: 'План', value: formatNum(plan), unit: 'Ph' },
    { label: 'Осталось', value: daysLeft, unit: 'дн' },
  ];

  const handleOpenEditPopup = () => {
    setIsEditPopupOpen(true);
  };

  const handleCloseEditPopup = () => {
    setIsEditPopupOpen(false);
  };

  const handleUpdateMonthlyPlan = async (planValue: string) => {
    const cleanPlan = planValue.replace(/\s/g, '') || '0';

    const bonusManager =
      teamMonthlyGoal?.is_bonus_active && teamMonthlyGoal?.bonus_manager_id
        ? teamMonthlyGoal.bonus_manager_id
        : null;

      await updateTeamMonthlyPlan.updateTeamMonthlyPlan({
        year,
        month,
        plan: cleanPlan,
        is_bonus_active: teamMonthlyGoal?.is_bonus_active ?? false,
        bonus_manager: bonusManager,
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.TEAM_MONTHLY_GOAL, year, month],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.TEAM_MONTHLY_RESULTS, year, month],
      });

    toast.success('Месячный план команды обновлен');
    setIsEditPopupOpen(false);
  };

  return (
    <div className="flex flex-col gap-8">
      <Typography
        size={TextSize.size24}
        className="font-semibold"
        title="Показатели команды"
      />
      <div className="grid grid-cols-[260px_380px] items-end gap-12">
        <div />
        <div className="justify-self-start">
          <ProgressGraph
            percent={percent}
            currentValue={currentValue}
            plan={plan}
            remainingPercent={remainingPercent}
            remainingPh={formatNum(remainingValue)}
            managers={managersData}
            totalHashrate={totalHashrate}
            monthly={true}
          />
        </div>
        <div className="flex items-end gap-8">
          <div className='flex items-baseline'>
            <div className="flex flex-col gap-[10px]">
              {infoCards.map(({ label, value, unit }) => (
                <div
                  key={label}
                  className="w-[224px] flex justify-between items-center bg-dark-200 rounded-md px-2 py-1"
                >
                  <Typography size={TextSize.size14} className="text-gray-400" text={`${label}:`} />
                  <div className="flex gap-1 items-start">
                    <Typography size={TextSize.size20} text={value} />
                    <span className="text-xs text-gray-400">{unit}</span>
                  </div>
                </div>
              ))}
            </div>
            <Pencil
              size={16}
              className="text-gray-400 cursor-pointer hover:opacity-70 ml-[42px]"
              onClick={handleOpenEditPopup}
            />
          </div>
        </div>
      </div>
      {isEditPopupOpen && (
        <EditMonthlyResult
          open
          value={planValue}
          onSubmit={handleUpdateMonthlyPlan}
          onClose={handleCloseEditPopup}
        />
      )}
    </div>
  );
};